import React, { useState } from 'react';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import './AgingPopulation.scss';

/**
 * 「香港人口老化預測」, an in-house replacement for the Piktochart of the same name, in the look of
 * ai.portfoplus.com: the Piktochart's bar chart of the share of people over 65 in 2050, and a row of
 * ten people that shows how many of them that is for the region picked. The figures are the
 * Piktochart's own (UN World Social Report 2023), kept as they are.
 */

type Region = 'hk' | 'kr' | 'jp' | 'it' | 'es';

/** Share of the population aged over 65 in 2050, per cent; the Piktochart's figures. */
export const SHARE: Record<Region, number> = { hk: 40.6, kr: 39.4, jp: 37.5, it: 37.1, es: 36.6 };
export const REGIONS: Region[] = ['hk', 'kr', 'jp', 'it', 'es'];

/** The bars are drawn against this, the axis of the Piktochart running to 40. */
const AXIS_MAX = 45;
const TICKS = [0, 10, 20, 30, 40];

const SOURCE =
  'https://desapublications.un.org/publications/world-social-report-2023-leaving-no-one-behind-ageing-world';

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}

/** The app's `zh` is Simplified Chinese; `lang` picks the CJK font in the stylesheet. */
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

const AgingPopulation = ({ intl }: { intl: Intl }) => {
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const [picked, setPicked] = useState<Region>('hk');

  /** Out of ten people, rounded, which is how the Piktochart reads 40.6% as 4. */
  const older = Math.round(SHARE.hk / 10);
  const pickedOlder = Math.round(SHARE[picked] / 10);

  return (
    <div className="aging-population" lang={HTML_LANG[intl.locale] || intl.locale}>
      <h2 className="ag-title">
        {t('ag-title-1')}
        <span>{t('ag-title-hl')}</span>
        {t('ag-title-2')}
      </h2>

      <section className="ag-chart">
        <h3>{t('ag-chart-title')}</h3>
        <ul className="ag-bars">
          {REGIONS.map(region => (
            <li key={region}>
              <button
                type="button"
                className={`ag-row is-${region}${region === picked ? ' is-on' : ''}`}
                aria-pressed={region === picked}
                onClick={() => setPicked(region)}
              >
                <span className="ag-name">{t(`ag-${region}`)}</span>
                <span className="ag-track">
                  <span className="ag-bar" style={{ width: `${(SHARE[region] / AXIS_MAX) * 100}%` }} />
                </span>
                <strong className="ag-value">{SHARE[region]}%</strong>
              </button>
            </li>
          ))}
        </ul>
        <div className="ag-axis" aria-hidden="true">
          <span className="ag-axis-pad" />
          <span className="ag-axis-scale">
            {TICKS.map(tick => (
              <i key={tick} style={{ left: `${(tick / AXIS_MAX) * 100}%` }}>
                {tick}
              </i>
            ))}
          </span>
        </div>
        <p className="ag-axis-label">{t('ag-axis')}</p>
      </section>

      <section className="ag-people">
        <p aria-live="polite">{t('ag-people', { region: t(`ag-${picked}`), n: pickedOlder })}</p>
        <div className="ag-row-people" aria-hidden="true">
          {Array.from({ length: 10 }, (_, i) => (
            <PersonRoundedIcon key={i} className={i < pickedOlder ? 'is-old' : ''} />
          ))}
        </div>
        <small>{t('ag-people-note')}</small>
      </section>

      <div className="ag-lesson">
        <strong>{t('ag-lesson', { n: older })}</strong>
      </div>

      <div className="ag-tip">
        <CampaignRoundedIcon aria-hidden="true" />
        <p>{t('ag-conclusion')}</p>
      </div>

      <p className="ag-source">
        {t('ag-source')}
        <a href={SOURCE} target="_blank" rel="noopener noreferrer">
          {t('ag-source-link')}
        </a>
      </p>
    </div>
  );
};

export default AgingPopulation;
